import React, { useState } from 'react';
import { Star, X, Send, Heart, AlertTriangle } from 'lucide-react';

const StarRating = ({ rating, onRatingChange, label }) => {
  return (
    <div className="flex items-center space-x-2">
      <span className="text-sm font-medium text-slate-700 w-24">{label}:</span>
      <div className="flex space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onRatingChange(star)}
            className="focus:outline-none"
          >
            <Star
              className={`w-5 h-5 ${
                star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
              } hover:text-yellow-400 transition-colors`}
            />
          </button>
        ))}
      </div>
      <span className="text-sm text-slate-500 ml-2">({rating}/5)</span>
    </div>
  );
};

const FeedbackModal = ({ isOpen, onClose, onSubmit }) => {
  const [feedback, setFeedback] = useState({
    overall_rating: 0,
    comments: '',
    ease_of_use_rating: 0,
    features_liked: '',
    accuracy_rating: 0,
    support_rating: 0,
    future_requirements: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous validation errors
    setValidationError('');
    
    // Validation
    if (feedback.overall_rating === 0 || feedback.ease_of_use_rating === 0 || 
        feedback.accuracy_rating === 0 || feedback.support_rating === 0) {
      setValidationError('Please provide all star ratings to submit your feedback');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(feedback);
      onClose();
      // Reset form
      setFeedback({
        overall_rating: 0,
        comments: '',
        ease_of_use_rating: 0,
        features_liked: '',
        accuracy_rating: 0,
        support_rating: 0,
        future_requirements: ''
      });
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Heart className="w-6 h-6" />
              <h2 className="text-xl font-bold">Share Your Experience</h2>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <p className="text-blue-100 mt-2">Help us improve Portfolio Auditor with your valuable feedback</p>
        </div>

        {/* Validation Error */}
        {validationError && (
          <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3 animate-pulse">
            <div className="bg-red-100 rounded-full p-2 mt-0.5">
              <AlertTriangle className="w-4 h-4 text-red-600" />
            </div>
            <div className="flex-1">
              <p className="text-red-800 text-sm font-medium">{validationError}</p>
              <p className="text-red-600 text-xs mt-1">⭐ All star ratings are required to submit feedback</p>
            </div>
            <button
              type="button"
              onClick={() => setValidationError('')}
              className="text-red-400 hover:text-red-600 p-1 rounded hover:bg-red-100 transition-colors"
              title="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Overall Rating */}
          <div className="bg-slate-50 rounded-xl p-4">
            <h3 className="font-semibold text-slate-900 mb-3">Overall Experience <span className="text-red-500">*</span></h3>
            <StarRating
              label="Overall"
              rating={feedback.overall_rating}
              onRatingChange={(rating) => {
                setFeedback({...feedback, overall_rating: rating});
                if (validationError) setValidationError(''); // Clear error when user starts rating
              }}
            />
          </div>

          {/* General Comments */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Overall Comments
            </label>
            <textarea
              value={feedback.comments}
              onChange={(e) => setFeedback({...feedback, comments: e.target.value})}
              placeholder="Tell us about your experience with Portfolio Auditor..."
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows="3"
            />
          </div>

          {/* Detailed Ratings */}
          <div className="bg-slate-50 rounded-xl p-4 space-y-4">
            <h3 className="font-semibold text-slate-900 mb-3">Rate Each Aspect <span className="text-red-500">*</span></h3>
            
            <StarRating
              label="Ease of Use"
              rating={feedback.ease_of_use_rating}
              onRatingChange={(rating) => {
                setFeedback({...feedback, ease_of_use_rating: rating});
                if (validationError) setValidationError(''); // Clear error when user starts rating
              }}
            />
            
            <StarRating
              label="Accuracy"
              rating={feedback.accuracy_rating}
              onRatingChange={(rating) => {
                setFeedback({...feedback, accuracy_rating: rating});
                if (validationError) setValidationError(''); // Clear error when user starts rating
              }}
            />
            
            <StarRating
              label="Support"
              rating={feedback.support_rating}
              onRatingChange={(rating) => {
                setFeedback({...feedback, support_rating: rating});
                if (validationError) setValidationError(''); // Clear error when user starts rating
              }}
            />
            <p className="text-xs text-slate-500 mt-2">* All ratings are required</p>
          </div>

          {/* Features Liked */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Features You Liked Most
            </label>
            <textarea
              value={feedback.features_liked}
              onChange={(e) => setFeedback({...feedback, features_liked: e.target.value})}
              placeholder="Which features did you find most valuable? (e.g., Apple-to-Apple comparison, Privacy, Tax reports...)"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows="2"
            />
          </div>

          {/* Future Requirements */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Future Feature Requests
            </label>
            <textarea
              value={feedback.future_requirements}
              onChange={(e) => setFeedback({...feedback, future_requirements: e.target.value})}
              placeholder="What features would you like to see in future versions?"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows="2"
            />
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-300 flex items-center space-x-2 disabled:opacity-50"
            >
              {isSubmitting ? (
                <span>Submitting...</span>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>Submit Feedback</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FeedbackModal;